import { useRef, useEffect } from 'react';

/**
 * Кастомный дебаунс-хук, позволяющий вызывать callback с задержкой,
 * а также отменять отложенный вызов (cancel) или принудительно вызвать немедленно (flush).
 *
 * @param {Function} callback - функция, которую вы хотите дебаунсить
 * @param {number} delay - задержка в мс
 * @returns {[debouncedFn: Function, flush: Function, cancel: Function]}
 *   debouncedFn(...)  -- вызывать вместо вашего callback (значения передаются внутрь callback с задержкой)
 *   cancel()          -- отменить (если таймер активен, не вызывать callback)
 *   flush()           -- вызвать callback немедленно с последними переданными аргументами
 */
export function useAdvancedDebounce(callback, delay) {
  const timerRef = useRef(null);
  const lastArgsRef = useRef([]);

  // Основная обёртка для callback: с каждым вызовом сбрасываем старый таймер и ставим новый
  const debouncedFn = (...args) => {
    // Сохраняем последние аргументы — они понадобятся при flush()
    lastArgsRef.current = args;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      callback(...args);
      timerRef.current = null;
    }, delay);
  };

  // Отмена отложенного вызова
  const cancel = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Мгновенный вызов callback с последними аргументами (и отмена таймера)
  const flush = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    callback(...lastArgsRef.current);
  };

  // При размонтировании компонента/хука — очистка таймера
  useEffect(() => cancel, []);

  return [debouncedFn, flush, cancel];
}
